import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActivityIndicator } from '../components/ActivityIndicator';
import { UniverseChrome } from '../components/UniverseChrome';
import { UniverseGrid } from '../components/UniverseGrid';
import { useAssetFeed, type FeedSource } from '../hooks/useAssetFeed';
import { useSortPreference } from '../hooks/useSortPreference';
import { track } from '../lib/analytics';
import { BROWSE_SORTS, DEFAULT_SORT, SEARCH_SORTS } from '../lib/sort';
import { resolveSort, saveSortPreference } from '../lib/sortPreference';
import { strings } from '../lib/strings';
import type { UniverseSort } from '../lib/types';

export default function DiscoveryPage() {
  const [params, setParams] = useSearchParams();
  const saved = useSortPreference();
  const query = (params.get('q') ?? '').trim();
  const searching = query.length > 0;
  const sort = resolveSort(params.get('sort'), saved, searching ? SEARCH_SORTS : BROWSE_SORTS);

  const source: FeedSource = searching
    ? { kind: 'search', q: query, sort }
    : { kind: 'browse', sort: sort === 'relevant' ? DEFAULT_SORT : sort };
  const feed = useAssetFeed(source);

  useEffect(() => {
    document.title = searching ? `${query} — ${strings.brand}` : strings.brand;
  }, [query, searching]);

  useEffect(() => {
    if (searching && !feed.pending && !feed.error) {
      track('search_results', { query_length: query.length, results: feed.total ?? 'unknown', sort });
    }
    // Once per settled result set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, query, sort, feed.pending]);

  const onSortChange = (value: UniverseSort) => {
    if (value !== sort) track('sort_changed', { from: sort, to: value, surface: searching ? 'search' : 'home' });
    saveSortPreference(value);
    // The saved choice now drives the order; a shared-link override no longer applies.
    if (params.has('sort')) {
      const next = new URLSearchParams(params);
      next.delete('sort');
      setParams(next, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-canvas">
      <UniverseChrome
        variant="masthead"
        query={query}
        sort={sort}
        sortOptions={searching ? SEARCH_SORTS : BROWSE_SORTS}
        onSortChange={onSortChange}
      />
      <main className="mx-auto max-w-page px-[var(--page-gutter)] pb-16 pt-6 sm:pb-24 sm:pt-8">
        <div
          className={`flex items-center gap-2 text-sm font-medium text-muted ${searching ? 'mb-6 h-6' : 'h-0'}`}
          aria-live="polite"
        >
          {searching && feed.pending && feed.items.length > 0 && (
            <>
              <ActivityIndicator size="sm" />
              <span>{strings.searching}</span>
            </>
          )}
          {searching && !feed.pending && !feed.error && feed.items.length > 0 && (
            <span key={query} className="rise-in">
              {strings.results(feed.total ?? feed.items.length)}
            </span>
          )}
        </div>
        <UniverseGrid
          feed={feed}
          label={searching ? query : strings.brand}
          analytics={{ surface: searching ? 'search' : 'home', sort, listKey: query }}
        />
      </main>
    </div>
  );
}
